import React, { useState, useCallback, useEffect } from "react";
import { useAuthentication } from "@welldigital/ui-common/Authentication/components/AuthenticationProvider";
import { AUTHENTICATED_STATE } from "@welldigital/ui-common/Authentication/components/consts";
import { Button, DetailsCard, Typography } from "@welldigital/components";
import { Box, CircularProgress, Container } from "@material-ui/core";
import { useStyles } from "../SignUpTargets/useStyles";
import { getCardsData } from "./cardsData";
import PageLayout from "../../layouts/PageLayout";
import { signUpURL } from "../../envDerivedConstants";

import ChevronRight from "@material-ui/icons/ChevronRight";
import { doRedirect } from "@welldigital/ui-common/Utils/navigation";
import { URLS } from "@welldigital/ui-common/Authentication/envDerivedConstants";
import { UserProfileLink } from "@welldigital/ui-common/Authentication/components/types";

const getParentShell = (
  renderableLinks: UserProfileLink[],
  initialLinks: UserProfileLink[]
) => {
  if (renderableLinks === initialLinks) return null;
  return initialLinks.find((link) => link.children === renderableLinks);
};

const UserTargets = (): JSX.Element => {
  const { availableLinks: initialAvailableLinks, authenticatedState } =
    useAuthentication();
  const [renderableLinks, setRenderableLinks] = useState<UserProfileLink[]>(
    initialAvailableLinks
  );
  const isUsingInitialLinks = initialAvailableLinks === renderableLinks;
  const updateRenderableLinks = useCallback((newLinks: UserProfileLink[]) => {
    setRenderableLinks(newLinks);
  }, []);

  const classes = useStyles();
  const cardsData = getCardsData(
    classes,
    renderableLinks,
    updateRenderableLinks,
    isUsingInitialLinks
  );

  const cardsDataEntries = [...Object.entries(cardsData)];
  const isUserHavingMultipleAccounts = cardsDataEntries.length > 1;
  const isUserHavingOnlyOneAccount = cardsDataEntries.length === 1;

  let defaultAccountURL = "";
  let defaultAccountTitle = "";
  if (isUserHavingOnlyOneAccount) {
    const defaultAccount = Object.values(renderableLinks)[0];
    defaultAccountURL = defaultAccount.href;
    defaultAccountTitle = defaultAccount.label;
  }

  const onReturnToSignInClick = useCallback(() => {
    doRedirect(URLS.loginEmailFormUrl, false);
  }, [history]);

  const onRegisterClick = useCallback(() => {
    doRedirect(signUpURL, false);
  }, [history]);

  // if
  //  - one account available redirect to defaultAccountURL
  useEffect(() => {
    if (
      !signUpURL ||
      authenticatedState !== AUTHENTICATED_STATE.YES ||
      !isUserHavingOnlyOneAccount
    ) {
      return;
    }
    setTimeout(() => {
      window.top.location.href = defaultAccountURL;
    }, 0);
  }, [authenticatedState]);

  const noAccountContent = (
    <Container maxWidth={"xs"}>
      <Typography align={"center"} className={classes.pageTitle}>
        It looks like you don’t have an account
      </Typography>
      <Box mt={3}>
        <Typography className={classes.cardContentText}>
          Please make sure you entered the email address that you used when you
          created your Well account. If you don’t already have an account you
          can create one now.
        </Typography>
        <Box mt={2}>
          <Button
            variant={"text"}
            style={{ marginRight: 20 }}
            minWidth={0}
            onClick={onReturnToSignInClick}
          >
            <Typography style={{ color: "black" }}>
              Return to Sign in
            </Typography>
          </Button>
          <Button
            variant={"contained"}
            color={"primary"}
            minWidth={230}
            onClick={onRegisterClick}
            endIcon={<ChevronRight />}
          >
            <Typography>Create an account</Typography>
          </Button>
        </Box>
      </Box>
    </Container>
  );

  const multiAccountContent = (
    <div>
      <Typography align={"center"} className={classes.pageTitle}>
        {isUsingInitialLinks
          ? "Which account do you want to view?"
          : `${
              getParentShell(renderableLinks, initialAvailableLinks)?.label
            } accounts`}
      </Typography>
      <Box className={classes.detailCardsWrapper}>
        {cardsDataEntries.map(([key, data]) => {
          return (
            <DetailsCard
              key={key}
              {...data.cardProps}
              className={classes.detailsCard}
            >
              {data.content}
            </DetailsCard>
          );
        })}
      </Box>
      {!isUsingInitialLinks && (
        <Box className={classes.detailCardsWrapper} pb={3}>
          <Button
            variant={"outlined"}
            color={"primary"}
            minWidth={230}
            onClick={updateRenderableLinks.bind(null, initialAvailableLinks)}
          >
            <Typography>Return to all accounts</Typography>
          </Button>
        </Box>
      )}
    </div>
  );

  const onlyOneAccountContent = (
    <div>
      <Typography align={"center"} className={classes.cardContentText}>
        {defaultAccountTitle} Redirecting....
      </Typography>
      <Box display={"flex"} justifyContent={"center"} pt={2}>
        <CircularProgress size={40} />
      </Box>
    </div>
  );

  const content = isUserHavingMultipleAccounts
    ? multiAccountContent
    : isUserHavingOnlyOneAccount
    ? onlyOneAccountContent
    : noAccountContent;

  return <PageLayout withUserProfile>{content}</PageLayout>;
};

export default UserTargets;
